import { useEffect, useRef } from 'react'
import * as ld from 'launchdarkly-js-client-sdk'
import { useLDClient, LDFlagChangeset } from 'launchdarkly-react-client-sdk'
import { shallowEqual } from 'react-redux'

import { useAppDispatch } from 'app/state'
import { updateFeatureFlagsFromLd, setFeatureFlagsFromLd, setLdStatus } from 'features/common/state/commonSlice'
import settings from 'settings'
import { localStorageSupported } from 'app/api/localStorage'
import { RequestStatus } from 'features/common/types'
import { useFallbackFeatureFlags } from './useFallbackFeatureFlags'

export const baseLdContext: ld.LDContext = {
  kind: 'user',
  version: settings.cartoVersion
}

const E2E_LD_USER_CONTEXT: ld.LDContext = {
  ...baseLdContext,
  key: 'e2e_user'
}

const ANONYMOUS_LD_USER_CONTEXT: ld.LDContext = {
  ...baseLdContext,
  key: 'anonymous_user'
}

const e2eLdsContextOverride =
  localStorageSupported() && window.localStorage.getItem('E2E_LOGIN_TOKEN') ? E2E_LD_USER_CONTEXT : undefined

export const initialLaunchDarklyContext = e2eLdsContextOverride || ANONYMOUS_LD_USER_CONTEXT

/**
 * The flow is seemingly complicated, so this is explanation:
 *
 * Second effect:
 *  * App initializes LD with `initialLaunchDarklyContext` which is usually just anynomous or e2e user
 *  * LD status is marked as fulfilled, and that works for contexts that are left in this state
 *
 * First effect:
 *  * Then, depending on route, we provide different context
 *      * for public map - it's only account context(builder public maps)
 *      * for all other authenticated routes, it's account + some other things
 *  * and first effects kicks-in, as new context is different than initial (anon/e2e) and we call identify and set status to pending
 *  * Then, depending on the response from LD, we update status to either fulfilled
 *
 * So, summarizing `ldStatus` is
 *   Fulfilled - for state of app initialization, flags are set in `fallbackFeatureFlags`
 *   Pending - when we know user/account and are waiting for LD to load flags
 *   Fulfilled - when LD returned actual flags for user/accunt
 */
export function useFeatureFlagsFromLd(ldContext_?: ld.LDContext) {
  const dispatch = useAppDispatch()
  const ldClient = useLDClient()
  const currentLaunchDarklyContext = useRef(initialLaunchDarklyContext)
  const fallbackFeatureFlags = useFallbackFeatureFlags()

  const ldContext = e2eLdsContextOverride || ldContext_ || ANONYMOUS_LD_USER_CONTEXT

  useEffect(() => {
    // If LaunchDarkly is disabled, we use the fallback FFs
    if (!settings.launchDarklyClientSideId) {
      dispatch(setFeatureFlagsFromLd(fallbackFeatureFlags))
      dispatch(setLdStatus(RequestStatus.Fulfilled))
      return
    }

    if (!ldClient) return

    const mergedNewContext = { ...baseLdContext, ...ldContext }
    if (shallowEqual(mergedNewContext, currentLaunchDarklyContext.current)) return

    currentLaunchDarklyContext.current = mergedNewContext

    dispatch(setLdStatus(RequestStatus.Pending))
    let timeoutId: number | undefined = setTimeout(() => {
      console.warn('timeout waiting for ldClient.identify')
    }, 10000) as unknown as number
    ldClient
      .identify(mergedNewContext)
      .then(() => {
        dispatch(setLdStatus(RequestStatus.Fulfilled))
      })
      .catch((e) => {
        // If LD fails, we use the fallback FFs
        console.error('ldClient.identify failed', e)
        dispatch(setFeatureFlagsFromLd(fallbackFeatureFlags))
        dispatch(setLdStatus(RequestStatus.Fulfilled))
      })
      .finally(() => {
        timeoutId = undefined
        clearTimeout(timeoutId)
      })
    return () => {
      clearTimeout(timeoutId)
    }
  }, [ldContext, ldClient, fallbackFeatureFlags, dispatch])

  useEffect(() => {
    if (!ldClient) return

    const handleFlagsChange = (flags: LDFlagChangeset) => {
      dispatch(updateFeatureFlagsFromLd(flags))
    }

    const ldFlags = ldClient.allFlags()
    dispatch(setFeatureFlagsFromLd(Object.keys(ldFlags).length > 0 ? ldFlags : fallbackFeatureFlags))
    dispatch(setLdStatus(RequestStatus.Fulfilled))

    ldClient.on('change', handleFlagsChange)

    return () => {
      ldClient.off('change', handleFlagsChange)
    }
  }, [ldClient, dispatch, fallbackFeatureFlags])
}
